import { useEffect, useMemo, useRef, useState } from 'react'
import { Cargando } from '../../componentes/Cargando'
import { EstadoError } from '../../componentes/EstadoError'
import { EstadoVacio } from '../../componentes/EstadoVacio'
import { IconoBuscar } from '../../componentes/iconos'
import { bonosDelCatalogo } from './catalogo'
import { useBuscarClientes, useCatalogo, useResolver } from './consultas'
import type { ClienteResuelto } from './consultas'
import type { FilaAgendamiento } from './filas'
import { formatearEuros } from './formato'
import { InsigniaMotivo } from './insignias'
import type { ExtraPedido } from './lista'
import type { ProductoElegido } from './SelectorProductos'
import { SelectorProductos } from './SelectorProductos'

/**
 * El modo individual: un alta suelta. Buscar el cliente, montar su pedido,
 * revisar y publicar.
 *
 * **El bono es opcional.** Se midio contra Odoo: 114 de los 478 pedidos reales
 * (el 23%) no llevan ninguno. Son garrafas sueltas, packs de 1,5 L, palets,
 * dispensadores, fianzas, cafe. Un pedido vale con bono, con productos sueltos
 * o con las dos cosas; lo unico que no vale es no pedir nada.
 *
 * El bono **no se escribe, se elige** de una lista de productos que existen en
 * el catalogo de Odoo. El backend vuelve a buscarlo por nombre al componer el
 * pedido y esa busqueda falla si el nombre casa con varios productos o con
 * ninguno; ofrecer solo lo que hay hace imposible ese fallo. En el modo masivo
 * el texto lo escribe una persona y el error es inevitable, y por eso alli se
 * marca la linea en vez de impedirlo.
 *
 * Al terminar, este modo produce **una fila** con la misma forma que produce el
 * masivo: de ahi en adelante los dos caminos son el mismo, y la comprobacion de
 * que la ficha esta elegida vive en un solo sitio.
 */

/** Sin espera, cada tecla seria una peticion al backend. */
const ESPERA_BUSQUEDA = 300

const CLASE_CAMPO =
  'w-full rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo'

interface Props {
  q: string
  cliente: string
  onBuscar: (q: string) => void
  onElegirCliente: (contactId: string) => void
  onFilas: (filas: FilaAgendamiento[]) => void
}

export function ModoIndividual({ q, cliente, onBuscar, onElegirCliente, onFilas }: Props) {
  const [texto, setTexto] = useState(q)
  const ultimoEnviado = useRef(q)
  const [bonoId, setBonoId] = useState('')
  const [productos, setProductos] = useState<ProductoElegido[]>([])

  const busqueda = useBuscarClientes(q)
  const catalogo = useCatalogo(cliente !== '')
  const resolver = useResolver()

  useEffect(() => {
    if (q === ultimoEnviado.current) return
    ultimoEnviado.current = q
    setTexto(q)
  }, [q])

  useEffect(() => {
    if (texto === ultimoEnviado.current) return
    const temporizador = setTimeout(() => {
      ultimoEnviado.current = texto
      onBuscar(texto)
    }, ESPERA_BUSQUEDA)
    return () => clearTimeout(temporizador)
  }, [texto, onBuscar])

  const catalogoItems = useMemo(() => catalogo.data?.items ?? [], [catalogo.data])
  const bonos = useMemo(() => bonosDelCatalogo(catalogoItems), [catalogoItems])

  const encontrados = busqueda.data?.items ?? []
  const elegido: ClienteResuelto | undefined = encontrados.find(
    (item) => item.cliente.contact_id === cliente,
  )
  const bono = bonos.find((candidato) => String(candidato.id) === bonoId)
  // Un pedido necesita algo que repartir, pero no necesariamente un bono.
  const hayPedido = bono !== undefined || productos.length > 0

  const revisar = () => {
    if (!elegido || !hayPedido) return
    resolver.mutate(
      {
        clientes: [
          {
            nif: elegido.cliente.code,
            nombre: elegido.cliente.nombre,
            // Sin bono viajan `null` los dos: el backend compone entonces solo
            // con los productos sueltos. Mandar uno sin el otro seria "media
            // peticion de bono" y devolveria un error, con razon.
            bono: bono ? bono.unidades : null,
            litraje: bono ? bono.litraje : null,
            variante: bono ? bono.variante : '',
            extras: productos.map<ExtraPedido>((producto) => ({
              nombre: producto.nombre,
              cantidad: producto.cantidad,
              product_id: producto.productoId,
            })),
          },
        ],
      },
      {
        onSuccess: (respuesta) => {
          const resultado = respuesta.resultados[0]
          if (!resultado) return
          onFilas([{ clave: 'individual', nif: elegido.cliente.code, resultado }])
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="max-w-lg">
        <label htmlFor="buscador-agendamiento" className="block text-sm font-medium text-tinta-2">
          Buscar cliente
        </label>
        <div className="relative mt-1">
          <IconoBuscar
            aria-hidden="true"
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-apagada"
          />
          <input
            id="buscador-agendamiento"
            type="search"
            autoComplete="off"
            placeholder="Nombre o NIF"
            value={texto}
            onChange={(evento) => setTexto(evento.target.value)}
            className={`${CLASE_CAMPO} pl-9`}
          />
        </div>
        <p className="mt-1 text-xs text-tinta-apagada">
          Solo se agenda a quien ya es cliente: esta pantalla no da de alta clientes nuevos.
        </p>
      </div>

      {busqueda.isError ? (
        <EstadoError error={busqueda.error} onReintentar={() => void busqueda.refetch()} />
      ) : busqueda.isFetching && encontrados.length === 0 ? (
        <Cargando mensaje="Buscando clientes" />
      ) : q.trim().length >= 2 && encontrados.length === 0 ? (
        <EstadoVacio
          titulo="Ningún cliente coincide"
          descripcion={`No hay ningún cliente cuyo nombre o NIF case con "${q}".`}
        />
      ) : encontrados.length > 0 ? (
        <ul aria-label="Clientes encontrados" className="flex flex-col gap-2">
          {encontrados.map((item) => {
            const activo = item.cliente.contact_id === cliente
            return (
              <li key={item.cliente.contact_id}>
                <button
                  type="button"
                  onClick={() => onElegirCliente(item.cliente.contact_id)}
                  aria-pressed={activo}
                  className={`flex w-full flex-wrap items-center gap-2 rounded-xl border px-4 py-3 text-left transition-colors ${
                    activo
                      ? 'border-acento-borde bg-acento-suave'
                      : 'border-borde bg-superficie hover:bg-elevado'
                  }`}
                >
                  <span className="text-sm font-medium text-tinta">{item.cliente.nombre}</span>
                  <span className="text-sm text-tinta-apagada">{item.cliente.code}</span>
                  <InsigniaMotivo motivo={item.motivo} />
                </button>
              </li>
            )
          })}
        </ul>
      ) : null}

      {elegido ? (
        <section
          aria-label="Pedido del cliente"
          className="flex flex-col gap-4 rounded-xl border border-borde bg-superficie px-4 py-4"
        >
          <div>
            <h2 className="text-sm font-semibold text-tinta">{elegido.cliente.nombre}</h2>
            <p className="text-sm text-tinta-apagada">
              {elegido.cliente.code}
              {elegido.cliente.direccion ? ` - ${elegido.cliente.direccion}` : ''}
            </p>
          </div>

          {catalogo.isError ? (
            <EstadoError error={catalogo.error} onReintentar={() => void catalogo.refetch()} />
          ) : catalogo.isPending ? (
            <Cargando mensaje="Cargando el catálogo" />
          ) : (
            <>
              <div className="max-w-lg">
                <label htmlFor="bono-individual" className="block text-sm font-medium text-tinta-2">
                  Bono y litraje
                </label>
                <select
                  id="bono-individual"
                  value={bonoId}
                  onChange={(evento) => setBonoId(evento.target.value)}
                  className={`${CLASE_CAMPO} mt-1`}
                >
                  <option value="">Sin bono</option>
                  {bonos.map((candidato) => (
                    <option key={candidato.id} value={candidato.id}>
                      {candidato.nombre} - {formatearEuros(candidato.precio)}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-tinta-apagada">
                  {bono
                    ? `${bono.unidades} garrafas de ${bono.litraje} L: se crearán la línea del bono, la de envases y la devolución en negativo.`
                    : 'Opcional: casi un cuarto de los pedidos no lleva bono. Monta el pedido con los productos de abajo.'}
                </p>
              </div>

              <SelectorProductos
                catalogo={catalogoItems}
                elegidos={productos}
                onCambiar={setProductos}
              />

              <button
                type="button"
                onClick={revisar}
                disabled={!hayPedido || resolver.isPending}
                className="self-start rounded-lg border border-acento-borde bg-acento-suave px-4 py-2 text-sm font-semibold text-acento-tinta transition-colors hover:bg-acento-tinta-suave disabled:cursor-not-allowed disabled:border-borde disabled:bg-desactivado disabled:text-desactivado-tinta"
              >
                {resolver.isPending ? 'Componiendo el pedido' : 'Revisar el pedido'}
              </button>
            </>
          )}

          {resolver.isError ? <EstadoError error={resolver.error} /> : null}
        </section>
      ) : null}
    </div>
  )
}
